import React from 'react';
import { Fragment } from 'react';

import parse, {
  attributesToProps,
  domToReact,
  HTMLReactParserOptions,
  Element,
  DOMNode,
} from 'html-react-parser';
import { allowedTags } from './allowedTags';
import { ContentProps, EnrichedData, ShortCodeProps } from './types';

export const parseHtmlToReact = ({
  content,
  post,
  path,
  shortcodesMap,
  enrichments,
  logger = { warn: console.log },
}: ContentProps & { enrichments: Record<string, EnrichedData> }) => {
  // This replaces shortcodes with react components
  const options: HTMLReactParserOptions = {
    replace: (domNode): React.ReactElement<unknown> | void => {
      const typedDomNode = domNode as Element;
      // Here we check if the domNode is a shortcode
      if (Object.keys(shortcodesMap).includes(typedDomNode.name)) {
        const Shortcode = shortcodesMap[typedDomNode.name];
        const Component = Shortcode.component;

        const enrichmentKey = typedDomNode.attribs?.['data-ek'] ?? '';
        const cleanAttribs = { ...typedDomNode.attribs };
        delete cleanAttribs['data-ek'];

        // Start with attributes from HTML
        let props: ShortCodeProps = {
          shortcode_name: typedDomNode.name,
          enrichments: {
            data: null,
            error: null,
          } as EnrichedData,
          ...attributesToProps(cleanAttribs),
        };

        // Merge props from meta if available
        if (Shortcode.getPropsFromMeta) {
          props = {
            ...props,
            ...Shortcode.getPropsFromMeta(post, path),
          };
        }

        // Merge props from content if available
        if (Shortcode.getPropsFromContent) {
          props = {
            ...props,
            ...Shortcode.getPropsFromContent(content),
          };
        }

        const enrichedData = enrichments?.[enrichmentKey];

        if (enrichedData) {
          props['enrichments'] = enrichedData;
        }

        const children = typedDomNode.children as DOMNode[];
        const kids = domToReact(children, options);

        return <Component {...props}>{kids}</Component>;
      }
    },
  };

  // Parse!
  let parsed;
  try {
    parsed = parse(content, options);
  } catch (err: unknown) {
    logger.warn('Failed to parse dirty string', err);
    return <></>;
  }

  // Return only allowed html tags
  if (Array.isArray(parsed)) {
    return (
      <>
        {parsed.map((el, index) => {
          if (el.type) {
            if (
              !allowedTags.includes(el.type) &&
              typeof el.type !== 'function'
            ) {
              return;
            }
          }
          return <Fragment key={index}>{el}</Fragment>;
        })}
      </>
    );
  } else {
    return <>{parsed}</>;
  }
};
